import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import { Container, Table } from "reactstrap";

import { Heading } from "components/Heading";
import { SearchInput } from "components/SearchInput";
import { Pagination } from "components/Pagination";
import { CustomModal } from "components/CustomModal";
import { CustomButton } from "components/CustomButton";
import { useLocalPaginatedRequest } from "hooks/useLocalPaginatedRequest";
import { PageHeadings, PageTitles } from "../../constants";
import { districtHubListApi, createDistrictApi, inviteDistrictOwnerApi } from "../../api/api.service";
import mindCircuitMark from "../../assets/images/district-hub/mind-circuit-mark.png";
import DistrictRegistrations from "./DistrictRegistrations";

/**
 * Ops console for the District Hub: give each district its first owner.
 *
 * The everyday action is on each row: "Invite owner" for a district that is
 * already listed (the imported districts are all here). It is addressed by the
 * district's id, so nothing has to be retyped. "Add a district" is only for a
 * district that isn't in the list at all.
 *
 * Creating a district stays ops-only: anyone may *register* a district that
 * exists (reviewed at the top of this page, DistrictRegistrations), but
 * nothing self-serve can make a district exist, which is what keeps someone from
 * standing up a page under another district's name.
 */

const STATUS = {
  DRAFT: { label: "Draft", className: "cs-badge cs-badge-pending" },
  PUBLISHED: { label: "Live", className: "cs-badge cs-badge-active" },
  PAUSED: { label: "Paused", className: "cs-badge cs-badge-pending" },
  ARCHIVED: { label: "Closed", className: "cs-badge cs-badge-blocked" },
};

const FILTERS = [
  { value: "waiting", label: "Waiting for an owner" },
  { value: "has_owner", label: "Have an owner" },
  { value: "", label: "All districts" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const daysLeft = (iso) => {
  const ms = new Date(iso).getTime() - Date.now();
  const d = Math.ceil(ms / 86400000);
  return d <= 1 ? "expires within a day" : `expires in ${d} days`;
};

// How many people have signed up as administrators of the district: the ones
// the team has approved, and the ones still waiting for review. Invitations
// nobody has accepted yet are not sign-ups, so they are not counted here.
const SignedUp = ({ d }) => {
  const { approved = 0, waiting = 0 } = d.signedUp || {};
  const total = approved + waiting;
  if (total === 0) return <span className="text-muted">None yet</span>;
  const parts = [approved ? `${approved} approved` : null, waiting ? `${waiting} waiting for review` : null];
  return (
    <div className="d-flex flex-column">
      <strong>{total} signed up</strong>
      <span className="text-muted font-size-12">{parts.filter(Boolean).join(" · ")}</span>
    </div>
  );
};

const OwnerState = ({ d }) => {
  if (d.hasApprovedOwner) return <span>Has an owner</span>;
  return (
    <div className="d-flex flex-column gap-1">
      {d.pendingInvite ? (
        <span>
          Invitation sent to <strong>{d.pendingInvite.email}</strong>
          <span className="text-muted"> · {daysLeft(d.pendingInvite.expiresAt)}</span>
        </span>
      ) : null}
      {d.pendingClaim ? (
        <span>
          Registered by someone, <a href="#registrations">waiting for your review</a>
        </span>
      ) : null}
      {!d.pendingInvite && !d.pendingClaim ? <span className="text-muted">No owner yet</span> : null}
    </div>
  );
};

const DistrictHub = () => {
  const [owner, setOwner] = useState("waiting");

  // Invite an owner to a listed district.
  const [inviting, setInviting] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);

  // Add a district that isn't listed.
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [similar, setSimilar] = useState([]);

  const {
    page,
    data,
    total,
    pageSize,
    isFetching,
    searchText,
    request: refetch,
    handleSearchChange,
    handlePageClick,
    onChangePageSize,
  } = useLocalPaginatedRequest(districtHubListApi, owner ? { owner } : {}, [owner]);

  const districts = Array.isArray(data) ? data : [];

  // Near-duplicate check while typing a new district's name. Debounced, and the
  // response is dropped if the name has moved on since it was requested.
  useEffect(() => {
    const q = name.trim();
    if (!creating || q.length < 3) {
      setSimilar([]);
      return undefined;
    }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const res = await districtHubListApi({ Q: q, limit: 5, page: 1 });
        if (live) setSimilar(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        if (live) setSimilar([]);
      }
    }, 300);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [name, creating]);

  // ------------------------------------------------------------ invite owner
  const openInvite = (d) => {
    setInviting(d);
    setInviteEmail("");
  };
  const closeInvite = () => {
    if (inviteBusy) return;
    setInviting(null);
    setInviteEmail("");
  };
  const inviteOk = EMAIL_RE.test(inviteEmail.trim()) && !inviteBusy;
  const sendInvite = async () => {
    if (!inviteOk || !inviting) return;
    setInviteBusy(true);
    try {
      await inviteDistrictOwnerApi({ id: inviting.id, ownerEmail: inviteEmail.trim() });
      toast.success(`Invitation to run ${inviting.name} sent to ${inviteEmail.trim()}.`);
      setInviting(null);
      setInviteEmail("");
      refetch();
    } catch (e) {
      toast.error(e?.message || "Could not send the invitation.");
    } finally {
      setInviteBusy(false);
    }
  };

  // ----------------------------------------------------------- add district
  const closeCreate = () => {
    if (submitting) return;
    setCreating(false);
    setName("");
    setOwnerEmail("");
    setSimilar([]);
  };
  const emailOk = EMAIL_RE.test(ownerEmail.trim());
  const canSubmit = name.trim().length >= 2 && emailOk && !submitting;
  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await createDistrictApi({ name: name.trim(), ownerEmail: ownerEmail.trim() });
      toast.success(`Invitation sent to ${ownerEmail.trim()}.`);
      setCreating(false);
      setName("");
      setOwnerEmail("");
      setSimilar([]);
      refetch();
    } catch (e) {
      toast.error(e?.message || "Could not add that district.");
    } finally {
      setSubmitting(false);
    }
  };
  const pickSimilar = (d) => {
    setCreating(false);
    setName("");
    setOwnerEmail("");
    setSimilar([]);
    openInvite(d);
  };

  return (
    <div className="page-content">
      <Helmet>
        <title>{PageTitles.DISTRICT_HUB}</title>
      </Helmet>
      <Container fluid>
        <Heading heading={PageHeadings.DISTRICT_HUB} />
        <p className="text-muted mb-4">
          Give each district its owner: find the district and press <strong>Invite owner</strong>. They get an email
          with a single-use link that works for 3 days. People who registered a district themselves are
          reviewed right here, above the list, whenever any are waiting.
        </p>

        <DistrictRegistrations onDecided={refetch} />

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <div className="btn-group" role="group" aria-label="Which districts">
            {FILTERS.map((f) => (
              <button
                key={f.value || "all"}
                type="button"
                className={`btn btn-sm ${owner === f.value ? "btn-primary" : "btn-outline-primary"}`}
                aria-pressed={owner === f.value}
                onClick={() => setOwner(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ms-auto d-flex align-items-center gap-2 flex-wrap">
            <div style={{ minWidth: 260 }}>
              <SearchInput placeholder="Search districts" value={searchText} onChange={handleSearchChange} />
            </div>
            <CustomButton title="Add a district" color="light" onClick={() => setCreating(true)} />
          </div>
        </div>

        <div className="table-responsive">
          <Table borderless className="mb-0 align-middle">
            <thead>
              <tr>
                <th>District</th>
                <th style={{ width: 110 }}>Hub page</th>
                <th>Owner</th>
                <th style={{ width: 190 }}>Signed up</th>
                <th style={{ width: 170 }} className="text-end">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isFetching && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    Loading…
                  </td>
                </tr>
              )}
              {!isFetching && districts.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    {searchText
                      ? `No district matches “${searchText}”. If it's new, use “Add a district”.`
                      : owner === "waiting"
                        ? "Every district has an owner."
                        : "No districts."}
                  </td>
                </tr>
              )}
              {!isFetching &&
                districts.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <img
                          src={d.logoUrl || mindCircuitMark}
                          alt=""
                          width={32}
                          height={32}
                          loading="lazy"
                          style={{ objectFit: "contain", borderRadius: 8, background: "#fff", flex: "none" }}
                          onError={(e) => {
                            // A moved logo file shows the Mind Circuit mark, never a broken image.
                            if (e.currentTarget.src !== mindCircuitMark) e.currentTarget.src = mindCircuitMark;
                          }}
                        />
                        <div>
                          <div>{d.name}</div>
                          {d.slug ? <div className="text-muted font-size-12">/districts/{d.slug}</div> : null}
                        </div>
                      </div>
                    </td>
                    <td>
                      {d.status ? (
                        <span className={STATUS[d.status]?.className || "cs-badge"}>{STATUS[d.status]?.label || d.status}</span>
                      ) : (
                        <span className="text-muted font-size-12">Not started</span>
                      )}
                    </td>
                    <td className="font-size-13">
                      <OwnerState d={d} />
                    </td>
                    <td className="font-size-13">
                      <SignedUp d={d} />
                    </td>
                    <td className="text-end">
                      <CustomButton
                        title={
                          d.hasApprovedOwner ? "Invite another owner" : d.pendingInvite ? "Send again" : "Invite owner"
                        }
                        color={d.hasApprovedOwner ? "light" : "primary"}
                        className="btn-sm"
                        onClick={() => openInvite(d)}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        {!!total && (
          <Pagination
            currentPage={page}
            totalData={total}
            rowLimit={pageSize}
            onPageClick={handlePageClick}
            handleChange={onChangePageSize}
          />
        )}

        {/* ------------------------------------------------ invite an owner */}
        <CustomModal isOpen={!!inviting} onClose={closeInvite} toggle={closeInvite} size="md">
          <div className="px-3 pb-3">
            <h5 className="mb-1">Invite an owner</h5>
            <p className="mb-3">
              <strong>{inviting?.name}</strong>
            </p>
            {inviting?.pendingInvite ? (
              <div className="alert alert-info font-size-12 py-2">
                An invitation to {inviting.pendingInvite.email} is waiting. Sending to the same address replaces that
                link; sending to someone else adds a second invitation.
              </div>
            ) : null}
            {inviting?.hasApprovedOwner ? (
              <div className="alert alert-warning font-size-12 py-2">
                This district already has an owner. The person you invite becomes an additional owner.
              </div>
            ) : null}
            <label className="form-label font-size-13 mb-1" htmlFor="invite-owner-email">
              Owner&apos;s email
            </label>
            <input
              id="invite-owner-email"
              type="email"
              className="form-control mb-1"
              autoFocus
              maxLength={254}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendInvite()}
              placeholder="superintendent@district.org"
            />
            {inviteEmail && !EMAIL_RE.test(inviteEmail.trim()) && (
              <div className="text-danger font-size-12 mb-2">Enter a valid email.</div>
            )}
            <p className="text-muted font-size-12 mt-2 mb-0">
              They get an email to set a password (or use their existing Mind Circuit account) and open the
              district&apos;s dashboard as its owner.
            </p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <CustomButton title="Cancel" color="light" onClick={closeInvite} disabled={inviteBusy} />
              <CustomButton title="Send invitation" color="primary" loading={inviteBusy} disabled={!inviteOk} onClick={sendInvite} />
            </div>
          </div>
        </CustomModal>

        {/* ----------------------------------------------- add a new district */}
        <CustomModal isOpen={creating} onClose={closeCreate} toggle={closeCreate} size="md">
          <div className="px-3 pb-3">
            <h5 className="mb-2">Add a district</h5>
            <p className="text-muted font-size-13">
              Only for a district that isn&apos;t in the list. For a listed one, close this and use its{" "}
              <strong>Invite owner</strong> button.
            </p>

            <label className="form-label font-size-13 mb-1" htmlFor="district-name">
              District name
            </label>
            <input
              id="district-name"
              className="form-control mb-2"
              autoFocus
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Austin Independent School District"
            />

            {similar.length > 0 && (
              <div className="alert alert-warning font-size-12 py-2">
                Already listed:
                <ul className="mb-0 ps-3 mt-1">
                  {similar.map((s) => (
                    <li key={s.id}>
                      <strong>{s.name}</strong>
                      {s.hasApprovedOwner ? " (has an owner)" : ""}{" "}
                      <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => pickSimilar(s)}>
                        Invite an owner to this one
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <label className="form-label font-size-13 mb-1" htmlFor="district-owner-email">
              Owner&apos;s email
            </label>
            <input
              id="district-owner-email"
              type="email"
              className="form-control mb-1"
              maxLength={254}
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="name@district.org"
            />
            {ownerEmail && !emailOk && <div className="text-danger font-size-12 mb-2">Enter a valid email.</div>}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <CustomButton title="Cancel" color="light" onClick={closeCreate} disabled={submitting} />
              <CustomButton
                title="Add and invite"
                color="primary"
                loading={submitting}
                disabled={!canSubmit}
                onClick={submit}
              />
            </div>
          </div>
        </CustomModal>
      </Container>
    </div>
  );
};

export default DistrictHub;
